import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { Gavel, Clock, Zap, Shield, MessageCircle, ArrowLeft } from "lucide-react";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import PinModal from "../components/PinModal";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => (url && !url.startsWith("http") ? `${BACKEND}${url}` : url);

const SUB_LABELS = { akun: "Akun", topup_via_id: "Top-up via ID", topup_via_login: "Top-up via Login", voucher: "Voucher", joki: "Joki" };
const GARANSI_LABELS = { akun: "Garansi 7 hari", topup_via_id: "Garansi 1 hari", topup_via_login: "Garansi 7 hari", voucher: "Garansi 6 jam", joki: "Garansi 7 hari" };

function Countdown({ endsAt, frozen }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (frozen) return <span className="text-[#7C6EFF] font-mono">FROZEN</span>;
  const r = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(r / 1000), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return <span className="font-mono text-[#FFD700]">{r > 0 ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : "ENDED"}</span>;
}

export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const wallet = useWallet();
  const [a, setA] = useState(null);
  const [bidAmount, setBidAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get(`/auctions/${id}`).then((r) => { setA(r.data); setBidAmount(r.data.min_next_bid); });
  useEffect(() => { refresh(); const t = setInterval(refresh, 8000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [id]);

  const [pinMode, setPinMode] = useState(null);
  const [pendingBid, setPendingBid] = useState(null);

  if (!a) return <div className="text-white/50 text-center py-32">Loading auction...</div>;

  const doBid = async (pin, amt, useBinPrice) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bid`, { amount: amt, pin });
      if (data.bin_triggered) {
        toast.success(`🎉 BUY NOW! Auction langsung menang dengan ${formatIDR(amt)}`);
      } else {
        toast.success(`Bid ${formatIDR(amt)} berhasil!`);
      }
      wallet.refresh();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bid failed");
    } finally { setBusy(false); }
  };

  const placeBid = (e, useBinPrice = false) => {
    e?.preventDefault();
    if (!user) return navigate("/login");
    const amt = useBinPrice ? a.buy_now_price : parseFloat(bidAmount);
    if (amt > (wallet.balance + (a.current_bid_user_id === user.id ? a.current_bid_amount : 0))) {
      if (window.confirm("Saldo kurang. Top-up sekarang?")) navigate("/wallet");
      return;
    }
    setPendingBid({ amount: amt, isBin: useBinPrice });
    setPinMode(user?.has_pin ? "verify" : "set");
  };

  const isWinning = a.current_bid_user_id === user?.id;
  const youWon = a.status === "sold" && a.winner_user_id === user?.id;

  return (
    <div data-testid="auction-detail-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <Link to="/auction" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 hover:text-white mb-6">
        <ArrowLeft className="w-3 h-3" /> Kembali ke Arena
      </Link>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
        {/* Image + info */}
        <div>
          <div className="aspect-[16/10] bg-black border border-white/10 overflow-hidden relative">
            {a.image_url && <img src={resolveImg(a.image_url)} alt={a.title} className="w-full h-full object-cover" />}
            <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
              <span className={`tag ${a.status === "live" ? "!border-[#00F0FF]/50 !text-[#00F0FF] !bg-[#00F0FF]/10" : a.status === "sold" ? "!border-[#00FF66]/50 !text-[#00FF66] !bg-[#00FF66]/10" : "!border-white/30 !text-white/60"}`}>{a.status.toUpperCase()}</span>
              {a.is_admin && <span className="tag" style={{ borderColor: "rgba(255,0,60,0.6)", color: "#FF003C", background: "rgba(255,0,60,0.1)" }}>ADMIN</span>}
              {a.is_instant && <span className="tag" style={{ borderColor: "rgba(0,255,102,0.6)", color: "#00FF66", background: "rgba(0,255,102,0.1)" }}>⚡ INSTANT DELIVERY</span>}
              {a.buy_now_price && <span className="tag" style={{ borderColor: "rgba(255,215,0,0.6)", color: "#FFD700", background: "rgba(255,215,0,0.15)" }}>BIN {formatIDR(a.buy_now_price)}</span>}
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex justify-between">
              <span className="tag tag-accent">{a.game_name}</span>
              <span className="flex items-center gap-1 text-xs uppercase tracking-widest text-white/80 bg-black/60 px-3 py-1 border border-white/15">
                <Clock className="w-3 h-3 text-[#FFD700]" /> <Countdown endsAt={a.ends_at} frozen={a.status === "frozen"} />
              </span>
            </div>
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-6">{a.title}</h1>
          <p className="text-white/60 mt-2">{a.seller_name} · {SUB_LABELS[a.sub_category]}</p>
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-[#00FF66]">
            <Shield className="w-3 h-3" /> {GARANSI_LABELS[a.sub_category]}
          </div>
          <div className="mt-6 border border-white/10 p-5">
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-2">Deskripsi</h3>
            <p className="text-white/80 whitespace-pre-wrap">{a.description}</p>
          </div>
          {youWon && a.account_details && (
            <div className="mt-4 border border-[#00FF66]/40 bg-[#00FF66]/5 p-5" data-testid="winner-account-details">
              <h3 className="text-xs uppercase tracking-widest text-[#00FF66] mb-2">⚡ Detail Akun (instant delivery)</h3>
              <pre className="text-[#00FF66] text-sm font-mono whitespace-pre-wrap">{a.account_details}</pre>
            </div>
          )}
        </div>

        {/* Bid panel */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="border border-white/10 p-6">
            <div className="grid grid-cols-2 gap-3 pb-4 border-b border-white/10">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">Current Bid</p>
                <p className="font-display font-black text-2xl text-[#00F0FF]">{formatIDR(a.current_bid_amount || a.starting_bid)}</p>
                {!a.current_bid_amount && <p className="text-[10px] text-white/30">Starting bid</p>}
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Bids</p>
                <p className="font-display font-black text-2xl text-white">{a.bid_count}</p>
                {a.anti_snipe_extensions > 0 && <p className="text-[10px] text-[#FF003C]">×{a.anti_snipe_extensions} ext.</p>}
              </div>
            </div>

            {a.status === "live" && user && a.seller_id !== user.id && a.is_active_hours && (
              <form onSubmit={placeBid} className="mt-4" data-testid="detail-bid-form">
                <label className="text-xs uppercase tracking-widest text-white/60">Bid (min {formatIDR(a.min_next_bid)})</label>
                <input data-testid="detail-bid-amount" type="number" min={a.min_next_bid} step={a.bid_increment || 1000} value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} className="w-full bg-black border border-white/15 focus:border-[#7C6EFF] text-white text-xl font-display font-bold px-4 py-3 mt-2" />
                <p className="text-[10px] text-white/40 mt-1">Saldo: <span className="text-[#00F0FF]">{formatIDR(wallet.balance)}</span></p>
                {(() => {
                  const step = a.bid_increment || 5000;
                  const mults = [1, 2, 5, 10];
                  return (
                    <div className="flex flex-wrap gap-1.5 mt-3" data-testid="detail-bid-quick-add">
                      {mults.map((m) => {
                        const inc = step * m;
                        return (
                          <button
                            key={m}
                            type="button"
                            data-testid={`detail-bid-add-${inc}`}
                            onClick={() => setBidAmount((prev) => Math.max(a.min_next_bid, (parseFloat(prev) || a.min_next_bid) + inc))}
                            className="text-[10px] px-3 py-1.5 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] uppercase tracking-widest"
                          >
                            +{formatIDR(inc).replace("Rp ", "")}
                          </button>
                        );
                      })}
                      <button type="button" onClick={() => setBidAmount(a.min_next_bid)} className="text-[10px] px-3 py-1.5 border border-[#FFD700]/40 hover:border-[#FFD700] text-[#FFD700] uppercase tracking-widest">
                        Min {formatIDR(a.min_next_bid).replace("Rp ", "")}
                      </button>
                    </div>
                  );
                })()}
                <button type="submit" data-testid="detail-bid-submit" disabled={busy} className="w-full mt-3 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3D2BFF] hover:bg-[#5C4DFF] text-white font-display font-black uppercase tracking-widest text-xs disabled:opacity-50">
                  <Gavel className="w-4 h-4" /> {busy ? "..." : "Place Bid"}
                </button>
                {a.buy_now_price && (
                  <button type="button" data-testid="detail-bin-submit" onClick={(e) => placeBid(e, true)} disabled={busy} className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10 font-display font-black uppercase tracking-widest text-xs disabled:opacity-50">
                    <Zap className="w-4 h-4" /> Buy Now {formatIDR(a.buy_now_price)}
                  </button>
                )}
                <p className="text-[10px] uppercase tracking-widest text-white/30 mt-3 text-center">Saldo dikunci · refund kalau kalah</p>
              </form>
            )}
            {a.status === "live" && (!user || !a.is_active_hours) && (
              <p className="mt-4 text-sm text-white/60 text-center py-3 border border-white/10">
                {!user ? <Link to="/login" className="text-[#00F0FF]">Login dulu untuk bid</Link> : "Diluar jam aktif (08:00-21:00 WIB)"}
              </p>
            )}
            {a.status === "frozen" && <p className="mt-4 text-center text-sm text-[#7C6EFF] py-3 border border-[#7C6EFF]/30">Auction FROZEN · tunggu 08:00 WIB</p>}
            {a.status === "sold" && (
              <div className="mt-4 text-center">
                <p className="text-[#00FF66] font-bold">SOLD — {formatIDR(a.winner_bid_amount)}</p>
                {youWon && <Link to={`/chat?to=${a.seller_id}&auction=${a.id}`} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black text-xs uppercase font-bold tracking-widest"><MessageCircle className="w-3 h-3" /> Chat Seller</Link>}
              </div>
            )}
            {isWinning && a.status === "live" && <p className="mt-3 text-center text-xs uppercase tracking-widest text-[#00FF66]">Kamu memimpin</p>}
            {user && a.seller_id === user.id && typeof a.est_net_payout === "number" && (
              <div className="mt-4 border border-[#00FF66]/30 bg-[#00FF66]/5 p-3 text-xs space-y-1" data-testid="seller-net-payout-panel">
                <p className="text-[10px] uppercase tracking-widest text-white/50">Estimasi Pencairan</p>
                <div className="flex justify-between text-white/70"><span>Harga saat ini</span><span>{formatIDR(a.current_bid_amount || a.starting_bid)}</span></div>
                <div className="flex justify-between text-white/70"><span>Fee admin</span><span className="text-[#FFD700]">{a.seller_fee_pct || 0}% · −{formatIDR(a.est_fee_amount || 0)}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-white/10"><span className="text-white">Cair ke saldo</span><span className="text-[#00FF66]" data-testid="seller-net-payout-value">{formatIDR(a.est_net_payout)}</span></div>
                <p className="text-[10px] text-white/40 pt-1">Dicairkan setelah masa garansi selesai.</p>
              </div>
            )}
          </div>

          {/* Bid history */}
          <div className="border border-white/10 p-6 mt-4">
            <h3 className="text-xs uppercase tracking-widest text-white/60 mb-3">Riwayat Bid ({a.bids?.length || 0})</h3>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {(a.bids || []).slice(0, 20).map((b, i) => (
                <div key={b.id} className="flex justify-between text-sm py-1 border-b border-white/5">
                  <span className="text-white/70">{i === 0 ? "👑 " : ""}{b.user_name}</span>
                  <span className={i === 0 ? "text-[#00F0FF] font-bold" : "text-white/60"}>{formatIDR(b.amount)}</span>
                </div>
              ))}
              {(!a.bids || a.bids.length === 0) && <p className="text-white/40 text-sm">Belum ada bid.</p>}
            </div>
          </div>
        </div>
      </div>
      {pinMode && pendingBid && (
        <PinModal
          mode={pinMode}
          isChange={false}
          title={pendingBid.isBin ? "Konfirmasi Buy Now" : "Konfirmasi Bid"}
          description={`${pendingBid.isBin ? "Buy Now" : "Bid"} ${formatIDR(pendingBid.amount)} untuk ${a.title}`}
          onClose={() => { setPinMode(null); setPendingBid(null); }}
          onSuccess={(pin) => doBid(pin, pendingBid.amount, pendingBid.isBin)}
        />
      )}
    </div>
  );
}
